import {useCallback, useEffect, useRef, useState} from 'react';

const SAMPLE_FILES = {
    'server:kokoro-af_heart': 'server-kokoro-af_heart.mp3',
    'server:kokoro-ef_dora': 'server-kokoro-ef_dora.mp3',
    'server:piper-de-thorsten': 'server-piper-de-thorsten.mp3'
};
export const fixedTtsSampleUrl = voice => SAMPLE_FILES[voice]
    ? `${process.env.PUBLIC_URL || ''}/assets/tts-previews/${SAMPLE_FILES[voice]}` : null;

// Samples are bundled recordings. This hook has no synthesis/API fallback and
// never loads audio in response to opening the sheet or changing article text.
export default function useFixedTtsSamplePreview({open, voice, onError}) {
    const audioRef = useRef(null);
    const sessionRef = useRef(0);
    const activeVoiceRef = useRef(null);
    const errorRef = useRef(onError);
    errorRef.current = onError;
    const [playing, setPlaying] = useState(false);
    const [loading, setLoading] = useState(false);
    const resetAudio = useCallback(() => {
        sessionRef.current += 1;
        activeVoiceRef.current = null;
        const audio = audioRef.current;
        if (audio) {
            audio.pause();
            audio.removeAttribute('src');
            audio.load();
        }
    }, []);
    const stop = useCallback(() => {
        resetAudio(); setPlaying(false); setLoading(false);
    }, [resetAudio]);
    useEffect(() => {if (!open) stop();}, [open, stop]);
    useEffect(() => () => resetAudio(), [resetAudio]);
    const play = useCallback(async sampleVoice => {
        stop();
        const url = fixedTtsSampleUrl(sampleVoice);
        const audio = audioRef.current;
        if (!open || !audio) return false;
        if (!url) {errorRef.current?.(); return false;}
        const session = sessionRef.current;
        activeVoiceRef.current = sampleVoice;
        setLoading(true);
        audio.src = url;
        audio.load();
        try {
            await audio.play();
            if (session !== sessionRef.current) return false;
            setPlaying(true);
            return true;
        } catch {
            if (session === sessionRef.current) {setPlaying(false); errorRef.current?.();}
            return false;
        } finally {
            if (session === sessionRef.current) setLoading(false);
        }
    }, [open, stop]);
    const toggle = useCallback(() => {
        const audio = audioRef.current;
        if (audio && activeVoiceRef.current === voice && !audio.paused && !audio.ended) {
            audio.pause(); setPlaying(false); return Promise.resolve(false);
        }
        return play(voice);
    }, [play, voice]);
    return {audioRef, playing, loading, play, toggle, stop, setPlaying};
}
